import _ from "lodash";

import { TOGGLE_ON_COMPARE_DROPDOWN } from "app/action_types/website_analysis_action-types";
import { FILTER_DEFAULT_SEPARATOR } from "app/pages/website-analysis/constants/constants";
import { booleanSearchActionsTypesEnumToString } from "app/pages/website-analysis/traffic-sources/search/booleanSearchUtility";
import {
    EBrandedNonBrandedValues,
    EOrganicPaidFilterValues,
} from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";
import type { IRule } from "./filters/MultiIncludeExclude/types";
import type { IWebsiteKeywordsPageTableTopContext } from "./WebsiteKeywordsPageContext";

type ParamValueType = string | number | any[];

const elementsAreDifferent = (prevList: any[], nextList: any[]) => {
    const orderedPrevVal = [...prevList].sort();
    const orderedNextVal = [...nextList].sort();

    return !_.isEqual(orderedPrevVal, orderedNextVal);
};

const hasValue = (val: ParamValueType) => {
    return !(
        (typeof val === "string" && val.trim() === "") ||
        (Array.isArray(val) && val.length === 0) ||
        _.isNil(val)
    );
};

const isNextParamsDifferent = (prevParams, nextParams) => {
    return Object.entries(nextParams).some(([paramName, paramValue]) => {
        if (!prevParams.hasOwnProperty(paramName) && hasValue(nextParams[paramName])) {
            return true;
        } else if (prevParams[paramName] !== nextParams[paramName]) {
            if (Array.isArray(nextParams[paramName]) && Array.isArray(prevParams[paramName])) {
                return elementsAreDifferent(prevParams[paramName], nextParams[paramName]);
            } else {
                // filters assign different falsy/empty (ex. null, [], "" ...) values to params,
                // therefore, check for all possible "undefined" values when comparing
                return hasValue(prevParams[paramName]) || hasValue(nextParams[paramName]);
            }
        }
    });
};

export const onChipAdd = (newChip) => {
    const chipKind = booleanSearchActionsTypesEnumToString(newChip.action);
    allTrackers.dispatchTrackEvent("Drop Down", "click", `add/${chipKind || ""}/${newChip.text || ""}`);
};

export const onChipRemove = (removeChip) => {
    const chipKind = booleanSearchActionsTypesEnumToString(removeChip.action);
    allTrackers.dispatchTrackEvent("Drop Down", "click", `remove/${chipKind || ""}/${removeChip.text || ""}`);
};

export const getWebsiteKeywordsPageTableTopContext: (
    args: Record<string, any>,
) => IWebsiteKeywordsPageTableTopContext = (args) => {
    const {
        isLoadingData,
        nextTableParams,
        initialFiltersStateObject,
        applyChangesToUrl,
        isCompare,
        chosenItems,
        sourcesFilter,
        channelsFilter,
        tableData,
        addTempParams,
        isLast28Days,
        isAdditionalDataLoading,
        dispatch,
    } = args;
    return {
        resetEnabled: !isLoadingData,
        onReset: () => {
            proNavigator.applyUpdateParams(
                Object.fromEntries(
                    Object.entries(nextTableParams)
                        .filter(([key, value]) => {
                            return !["country", "duration", "webSource", "key"].includes(key);
                        })
                        .map(([key, value]) => [key, null]),
                ),
            );
            allTrackers.dispatchTrackEvent("Button", "click", "Reset filters");
        },
        applyEnabled: !isLoadingData && isNextParamsDifferent(initialFiltersStateObject.current, nextTableParams),
        onApply: () => {
            applyChangesToUrl();
        },
        isCompare,
        chosenItems: chosenItems,
        tableFilters: nextTableParams,
        searchEngines: sourcesFilter,
        searchTypes: channelsFilter,
        tableData: tableData,
        onAdvancedFilterToggle: () => {
            allTrackers.trackEvent("drop down", "open", `Keyword analysis advanced filter`);
            dispatch({ type: TOGGLE_ON_COMPARE_DROPDOWN });
        },
        onAdvancedFilterDone: (item) => {
            if (Array.isArray(item)) {
                addTempParams({
                    limits: item
                        .map((v) =>
                            v
                                ? `${v
                                      .map((i) => `${i / 100}-`)
                                      .join("")
                                      .slice(0, -1)};`
                                : `;`,
                        )
                        .join(""),
                });
            } else {
                const urlParams: any = {
                    limits: item && item.id,
                };
                if (item && item.id === "opportunities") {
                    urlParams.IncludeNoneBranded = true;
                    urlParams.IncludeBranded = false;
                }
                addTempParams(urlParams);
            }
        },
        onAdvancedFilterClose: () => {
            addTempParams({
                limits: null,
            });
        },
        onOrganicPaidChange: (item) => {
            addTempParams({
                IncludeOrganic: item?.id === EOrganicPaidFilterValues.IncludeOrganic || false,
                IncludePaid: item?.id === EOrganicPaidFilterValues.IncludePaid || false,
            });
            item &&
                allTrackers.dispatchTrackEvent(
                    "Filter Table",
                    "click",
                    `Filters/OrganicAndPaid/${EOrganicPaidFilterValues[item.id] || ""}`,
                );
        },
        onBrandedNonBrandedChange: (item) => {
            addTempParams({
                IncludeBranded: item?.id === EBrandedNonBrandedValues.IncludeBranded || false,
                IncludeNoneBranded: item?.id === EBrandedNonBrandedValues.IncludeNoneBranded || false,
            });
            item &&
                allTrackers.dispatchTrackEvent(
                    "Filter Table",
                    "click",
                    `Filters/BrandedNonBranded/${EBrandedNonBrandedValues[item.id] || ""}`,
                );
        },
        onIntentChange(items) {
            if (items?.length === 0) {
                addTempParams({
                    intentsfilter: null,
                });
            }
            const intentsfilter = items.reduce((acc, item) => (acc ? `${acc},${item.text}` : `${item.text}`), "");
            allTrackers.dispatchTrackEvent("Drop Down", "click", `apply initial filter/intent/${intentsfilter || ""}`);
            addTempParams({ intentsfilter });
        },
        onSearchChannelChange: (items) => {
            if (items?.length === 0) {
                addTempParams({
                    family: null,
                });
            } else {
                const family = items.reduce(
                    (acc, item) => (acc ? `${acc}${FILTER_DEFAULT_SEPARATOR}"${item.id}"` : `"${item.id}"`),
                    "",
                );

                addTempParams({ family });
                allTrackers.dispatchTrackEvent("Filter Table", "click", `Filters/SearchEngine/${family || ""}`);
            }
        },
        onSearchTypeChange: (items) => {
            if (items?.length === 0) {
                addTempParams({
                    source: null,
                });
            } else {
                const source = items.reduce(
                    (acc, id) => (acc ? `${acc}${FILTER_DEFAULT_SEPARATOR}${id}` : `${id}`),
                    "",
                );

                addTempParams({ source });
                allTrackers.dispatchTrackEvent("Filter Table", "click", `Filters/SearchType/${source || ""}`);
            }
        },
        onSearchEngineChange: (items) => {
            if (items?.length === 0) {
                addTempParams({
                    family: null,
                });
            } else {
                const family = items.reduce(
                    (acc, id) => (acc ? `${acc}${FILTER_DEFAULT_SEPARATOR}${id}` : `${id}`),
                    "",
                );

                addTempParams({ family });
                allTrackers.dispatchTrackEvent("Filter Table", "click", `Filters/SearchEngine/${family || ""}`);
            }
        },
        onChangeNewlyDiscovered: (value) => {
            addTempParams({
                IncludeNewKeywords: value,
            });
            value && allTrackers.dispatchTrackEvent("Filter Table", "switch", `NewlyDiscovered ${value || ""}`);
        },
        onChangeFilterChange: (value) => {
            addTempParams({
                Change: value,
            });
            value && allTrackers.dispatchTrackEvent("Filter Table", "switch", `ClicksChange/${value || ""}`);
        },
        onChangeTrending: (value) => {
            addTempParams({
                IncludeTrendingKeywords: value,
            });
            value && allTrackers.dispatchTrackEvent("Filter Table", "switch", `TrendingTerms ${value || ""}`);
        },
        onChangeQuestions: (value) => {
            addTempParams({
                IncludeQuestions: value,
            });
            value && allTrackers.dispatchTrackEvent("Filter Table", "switch", `QuestionQueries ${value || ""}`);
        },
        setNewDifficultyRange: ([difficultyFromValue, difficultyToValue]) => {
            allTrackers.dispatchTrackEvent(
                "Filter Table",
                "switch",
                `Filters/Difficulty/from:${difficultyFromValue || ""} to:${difficultyToValue || ""}`,
            );
            addTempParams({
                difficultyFromValue,
                difficultyToValue,
            });
        },
        onVolumeChange: (value) => {
            const { fromValue, toValue } = value;
            addTempParams({
                volumeFromValue: fromValue,
                volumeToValue: toValue,
            });
            if (fromValue || toValue) {
                allTrackers.dispatchTrackEvent(
                    "Filter Table",
                    "switch",
                    `Filters/Volume/from:${fromValue || ""} to:${toValue || ""}`,
                );
            }
        },
        onOpenVolume: () => {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "Open volume filter");
        },
        onOpenCpc: () => {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "Open CPC filter");
        },
        onCpcChange: (value) => {
            const { fromValue, toValue } = value;
            addTempParams({
                cpcFromValue: fromValue,
                cpcToValue: toValue,
            });
            if (fromValue || toValue) {
                allTrackers.dispatchTrackEvent(
                    "Filter Table",
                    "switch",
                    `Filters/Cpc/from:${fromValue || ""} to:${toValue || ""}`,
                );
            }
        },
        onPhraseChange: (value) => {
            addTempParams({
                selectedPhrase: value,
            });
            value && allTrackers.dispatchTrackEvent("Filter Table", "click", `Filters/Phrase/${value || ""}`);
        },
        isLast28Days: isLast28Days,
        onSerpFilterApply: (features) => {
            addTempParams({
                serp: features,
            });
        },
        onMultiIncludeExcludeKeywordChange: (rules: IRule[]) => {
            const value = JSON.stringify(rules);
            const prevValue = nextTableParams.multiIncludeExcludeKeywords;
            if (value !== prevValue) {
                addTempParams({
                    multiIncludeExcludeKeywords: value,
                });
            }
        },
        onMultiIncludeExcludeWebsiteChange: (rules: IRule[]) => {
            const value = JSON.stringify(rules);
            const prevValue = nextTableParams.multiIncludeExcludeWebsites;
            if (value !== prevValue) {
                addTempParams({
                    multiIncludeExcludeWebsites: value,
                });
            }
        },
        isAdditionalDataLoading,
    };
};
